import { LinkIcon, RectangleEllipsisIcon } from "lucide-react";

import type { AuthnDialogFlow } from "@/web/auth/useAuthnDialogFlow";
import { Button, type ButtonProps } from "@/web/components/primitives/button";
import { Input } from "@/web/components/primitives/input";
import { Spinner } from "@/web/components/primitives/spinner";
import { env } from "@/web/lib/env";
import { cn } from "@/web/lib/styling";

export type AuthnFormProps = {
  flow: AuthnDialogFlow;
};

export function AuthnForm({ flow }: AuthnFormProps) {
  const {
    state: {
      selectedMethod,
      emailSent,
      emailCodeAwaitingVerify,
      pendingEmailAction,
      isSocialLoading,
      errorMessage,
      defaultEmail,
    },
    actions: {
      setSelectedMethod,
      handleGoogleAuth,
      handleSendMagicLink,
      handleSendEmailCode,
      handleVerifyEmailCode,
    },
    refs: { emailInputRef, codeInputRefCallback },
  } = flow;

  const emailBusy = pendingEmailAction === "send-link" || pendingEmailAction === "send-code";
  const verifyBusy = pendingEmailAction === "verify";
  const emailActionDisabled = emailSent || emailCodeAwaitingVerify || emailBusy;

  return (
    <div className="relative min-h-60 w-full">
      <div
        className={cn(
          "absolute inset-4 flex items-center justify-center transition-all",
          selectedMethod === null
            ? "translate-x-0 opacity-100"
            : "pointer-events-none translate-x-[-10%] opacity-0",
        )}
      >
        <div className="flex w-full max-w-[320px] flex-col items-center justify-center gap-4">
          <GoogleSignInButton
            actionVerb="Continue"
            isSocialLoading={isSocialLoading}
            onClick={handleGoogleAuth}
            className="h-12 w-full text-[16px] font-semibold text-primary"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-12 w-full text-[16px] font-semibold text-primary"
            onClick={() => setSelectedMethod("email")}
          >
            Continue with email
          </Button>
        </div>
      </div>
      <div
        className={cn(
          "absolute inset-4 transition-all",
          selectedMethod === "email"
            ? "translate-x-0 opacity-100"
            : "pointer-events-none translate-x-[10%] opacity-0",
        )}
      >
        <form
          className="mx-auto flex w-full max-w-[320px] flex-col gap-2"
          onSubmit={emailCodeAwaitingVerify ? handleVerifyEmailCode : (e) => e.preventDefault()}
        >
          {emailCodeAwaitingVerify ? (
            <>
              <label htmlFor="authn-dialog-otp" className="sr-only">
                Six-digit verification code
              </label>
              <Input
                id="authn-dialog-otp"
                key="authn-otp-field"
                name="code"
                ref={codeInputRefCallback}
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                autoComplete="one-time-code"
                enterKeyHint="go"
                placeholder="000000"
                minLength={6}
                maxLength={6}
                required
                spellCheck={false}
                aria-describedby="authn-dialog-otp-hint"
                className="text-center text-lg font-medium"
                onChange={(e) => {
                  const el = e.currentTarget;
                  const digits = el.value.replace(/\D/g, "").slice(0, 6);
                  if (el.value !== digits) {
                    el.value = digits;
                  }
                }}
              />
              <p id="authn-dialog-otp-hint" className="text-sm">
                Enter the 6-digit code we sent to your email.
              </p>
            </>
          ) : (
            <>
              <label htmlFor="authn-dialog-email" className="sr-only">
                Email address
              </label>
              <Input
                id="authn-dialog-email"
                key="authn-email-field"
                className="w-full"
                type="email"
                name="email"
                autoComplete="email"
                placeholder="Email"
                required
                // Uncontrolled: the flow reads the live value off the ref, so a
                // pre-filled address stays fully editable if we got it wrong.
                defaultValue={defaultEmail}
                ref={emailInputRef}
              />
            </>
          )}
          {emailCodeAwaitingVerify ? (
            <Button
              type="submit"
              color="blue"
              isLoading={verifyBusy}
              disabled={verifyBusy}
              className="h-10 w-full"
            >
              Verify
            </Button>
          ) : (
            <>
              <Button
                type="button"
                color="blue"
                isLoading={pendingEmailAction === "send-code"}
                disabled={emailActionDisabled}
                className="h-10 w-full"
                onClick={() => void handleSendEmailCode()}
              >
                <RectangleEllipsisIcon />
                Send code
              </Button>
              <Button
                type="button"
                variant="outline"
                isLoading={pendingEmailAction === "send-link"}
                disabled={emailActionDisabled}
                className="h-10 w-full"
                onClick={() => void handleSendMagicLink()}
              >
                <LinkIcon />
                {emailSent ? "Link sent" : "Send link"}
              </Button>
            </>
          )}
          <Button
            type="button"
            variant="outline"
            className="h-10 w-full"
            disabled={emailBusy || verifyBusy}
            onClick={() => setSelectedMethod(null)}
          >
            Back
          </Button>
          {emailSent && !emailCodeAwaitingVerify ? (
            <p className="text-sm">Check your email for a magic link to continue.</p>
          ) : null}
          {errorMessage ? <p className="text-sm text-withus-red">{errorMessage}</p> : null}
          {/* Placeholder for Clerk's bot-protection CAPTCHA widget. Must be in the DOM before
              signUp.create() runs; without it, Clerk silently falls back to an invisible
              challenge that blocks any flagged user with no recourse. */}
          <div id="clerk-captcha" data-cl-theme="auto" data-cl-size="flexible" />
        </form>
      </div>
    </div>
  );
}

function GoogleSignInButton({
  actionVerb,
  isSocialLoading,
  onClick,
  className,
  ...props
}: ButtonProps & { actionVerb: string; isSocialLoading: boolean }) {
  return (
    <Button
      onClick={onClick}
      variant="outline"
      type="button"
      className={cn("flex items-center gap-2", className)}
      disabled={isSocialLoading}
      {...props}
    >
      {isSocialLoading ? (
        <Spinner size={"small"} />
      ) : (
        <>
          <img
            src={`${env.VITE_WITHCO_IMAGE_CDN_BASE}/tr:w-64/google_icon.png`}
            alt="Google"
            className="size-6"
          />
          {actionVerb} with Google
        </>
      )}
    </Button>
  );
}
